<template>
  <div>
    <!-- DISPLAY CITATION -->
    <div uk-modal ref="citationFormModal" id="citationFormModal">
      <BasicModal
        id="moveableCitaiton"
        :modalId="'modalHeaderMain'"
        style="width: 40%"
        :buttons="citationModalButtons"
        :actionStatus="actionStatus"
        :heading="{
          title: citationModalTitle,
          customIcon: '/icons/white/icon-white-citation.svg',
          color: 'white',
          expandShow: expandShow,
        }"
        :bottomLine="false"
        :showClose="true"
        class="citemodalPosition"
      >
        <CitationForm
          ref="citationForm"
          :citation="citation"
          :mode="mode"
          @invalidated="citationFormInvalid"
          @expandShowButton="expandShowButton"
        />
      </BasicModal>
    </div>

    <!-- TO ANNOTATE -->
    <div uk-modal ref="toAnnotateModal" id="toAnnotateModal">
      <BasicModal
        class="uk-padding-remove uk-width-2-3@s uk-width-1-3@xl moveableBasis"
        :showClose="false"
        style="width: 847px"
        id="moveableBasisForm"
        :resizeModal="true"
      >
        <NewBasisForm
          ref="basisForm"
          :submitDiscussionButtonStatus="submitDiscussionStatus"
          :formConfig="newBasisFormConfig"
          :citationFramingModal="true"
          @submit="doAnnotateCitation"
          @close="closeToAnnotateModal"
        />
      </BasicModal>
    </div>
  </div>
</template>

<script>
import { getId, dragElement } from '@/assets/js/utils';
import { EventBus } from '@/assets/js/event-bus';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import BasicModal from '@/components/display/general/BasicModal';
import CitationForm from '@/components/form/CitationForm.vue';
import DocumentType from '@/assets/js/enum/DocumentType';
import NewBasisForm from '@/components/form/NewBasisForm.vue';
import UIkit from 'uikit';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'CitationModal',

  components: {
    BasicModal,
    CitationForm,
    NewBasisForm,
  },

  data() {
    return {
      mode: '',
      parent: null,
      parentId: null,
      parentType: null,
      citation: null,
      originalCitationData: null,
      actionStatus: '',
      expandShow: false,
      submitDiscussionStatus: LoadingStatus.NONE,
      citationModalButtonsDef: {
        Close: {
          action: () => UIkit.modal(this.$refs.citationFormModal).hide(),
        },
        Primary: {
          action: () => (this.mode === 'review' ? this.goToAnnotate() : this.submitCitationForm()),
        },
      },

      newBasisFormConfig: {
        initialStance: {
          visible: false,
        },
        documentType: {
          visible: false,
          editable: false,
        },
        contentFormat: {
          visible: false,
        },
      },
    };
  },
  // this.submitToAnnotateForm
  computed: {
    UIkit() {
      return UIkit;
    },
    DocumentType() {
      return DocumentType;
    },
    LoadingStatus() {
      return LoadingStatus;
    },

    ...mapState(['documentData', 'citations', 'publicView', 'userData', 'debateNextState']),

    /**
     * Title for main citation popup modal (citationFormModal)
     */
    citationModalTitle() {
      switch (this.mode) {
        case 'new':
          return `New Citation`;
        case 'edit':
          return `Edit Citation`;
        case 'review':
          return `Review Citation`;
        default:
          return 'View Citation';
      }
    },

    /**
     * Buttons for main citation popup modal (citationFormModal)
     */
    citationModalButtons() {
      let data = this.citation?.url?.includes('debate');
      if (this.citation?.disputed || this.mode === 'readOnly')
        return [
          {
            label: 'Close',
            ...this.citationModalButtonsDef.Close,
          },
        ];
      else
        return [
          {
            label: this.closeButtonText,
            ...this.citationModalButtonsDef.Close,
          },
          {
            label: this.submitButtonText,
            disabled: this.mode === 'new' || data,
            dis: data && this.mode !== 'new' && this.mode !== 'edit',
            tooltip:
              data && this.mode !== 'new' && this.mode !== 'edit'
                ? 'Critiquing previous debates is for future release'
                : this.mode === 'review'
                  ? 'Start a sub-debate'
                  : '',
            style: this.mode === 'review' ? 'orange' : 'primary',
            ...this.citationModalButtonsDef.Primary,
          },
        ];
    },

    closeButtonText() {
      let action = '';

      if (this.mode === 'review') return 'Close';

      if (this.mode === 'new') {
        action = 'Cancel';
      } else if (this.mode === 'edit') {
        action = 'Cancel';
      }

      return `${action} `;
    },

    submitButtonText() {
      let action = '';

      if (this.mode === 'review') return 'Make a Critique';

      if (this.mode === 'new') {
        action = 'Add Citation';
      } else if (this.mode === 'edit') {
        action = 'Update';
      }

      return `${action} `;
    },
  },

  mounted() {
    EventBus.on('citation.new', this.showNewCitation);
    EventBus.on('citation.edit', this.showCitationForEdits);
    EventBus.on('citation.review', this.showCitationForReview);
    EventBus.on('citation.readOnly', this.showCitationReadOnly);
    EventBus.on('citation.remove', this.removeCitation);

    EventBus.on('citation.saved', this.citationSaved);

    EventBus.on('citation.toAddNew', this.doAddNewCitation);
    EventBus.on('citation.toUpdate', this.doUpdateCitation);
    UIkit.util.on(this.$refs.citationFormModal, 'hide', async () => {
      EventBus.emit('citation.modal.hide', false);
      if (this.mode === 'edit') {
        EventBus.emit('citation.edit.modal.hide', true);
      } else {
        EventBus.emit('citation.edit.modal.hide', false);
      }
    });
  },

  beforeUnmount() {
    EventBus.off('citation.new', this.showNewCitation);
    EventBus.off('citation.edit', this.showCitationForEdits);
    EventBus.off('citation.review', this.showCitationForReview);
    EventBus.off('citation.readOnly', this.showCitationReadOnly);
    EventBus.off('citation.remove', this.removeCitation);

    EventBus.off('citation.saved', this.citationSaved);

    EventBus.off('citation.toAddNew', this.doAddNewCitation);
    EventBus.off('citation.toUpdate', this.doUpdateCitation);
  },

  methods: {
    ...mapActions(['loadCitationDocument', 'deleteCitation', 'addCitation', 'updateCitation']),
    expandShowButton(value) {
      this.expandShow = value;
    },
    /**
     * Setup general citation values necessary for this component
     */
    assignValues({ citation, parent, parentType }) {
      this.parent = parent;
      this.parentType = parentType;
      this.parentId = parentType === DocumentType.OUTLINE ? parent.nodeKey : parent._id;
      this.citation = citation;
    },

    /**
     * Show empty form for creating a new citation
     */
    showNewCitation(parent, parentType) {
      this.actionStatus = LoadingStatus.NONE;
      this.mode = 'new';
      this.assignValues({
        citation: null,
        parent,
        parentType,
      });
      this.$refs.citationForm.resetForm();
      UIkit.modal(this.$refs.citationFormModal, { center: true, stack: true }).show();
      dragElement(document.getElementById('moveableCitaiton'), 'modalHeaderMain');
    },

    /**
     * Show populated editable form with the contents of a provided citation
     */
    showCitationForEdits(citation, parent, parentType) {
      this.actionStatus = LoadingStatus.NONE;
      this.mode = 'edit';
      this.assignValues({
        citation,
        parent,
        parentType,
      });
      this.originalCitationData = { ...citation };
      this.$refs.citationForm.setCitationData(this.originalCitationData);
      setTimeout(() => {
        EventBus.emit('citation.edit.modal.hide', true);
      }, 100);
      UIkit.modal(this.$refs.citationFormModal, { center: true, stack: true }).show();
      EventBus.emit('citatiom.mode.model', this.mode, citation);
      dragElement(document.getElementById('moveableCitaiton'), 'modalHeaderMain');
    },
    /**
     * Show populated editable form with the contents of a provided citation for review
     */
    showCitationForReview(citation, parent, parentType) {
      this.actionStatus = LoadingStatus.NONE;
      this.mode = 'review';
      this.assignValues({
        citation,
        parent,
        parentType,
      });
      UIkit.modal(this.$refs.citationFormModal, { center: true, stack: true }).show();
      dragElement(document.getElementById('moveableCitaiton'), 'modalHeaderMain');
    },

    /**
     * Show populated non-editable form with the contents of a provided citation
     */
    showCitationReadOnly(citation, parent, parentType) {
      this.actionStatus = LoadingStatus.NONE;
      this.mode = 'readOnly';
      this.assignValues({
        citation,
        parent,
        parentType,
      });
      UIkit.modal(this.$refs.citationFormModal, { center: true, stack: true }).show();
      dragElement(document.getElementById('moveableCitaiton'), 'modalHeaderMain');
    },

    /**
     * Actions performed after a citation has been successfully saved
     */
    citationSaved() {
      this.savingStatus = LoadingStatus.SUCCESS;
      switch (this.mode) {
        case 'new':
          this.mode = 'edit';
          break;
        case 'edit':
          UIkit.modal(this.$refs.citationFormModal).hide();
          break;
        case 'annotate':
          UIkit.modal(this.$refs.newCitationModal).hide();
          break;
        case 'remove':
          UIkit.modal(this.$refs.removeCitationModal).hide();
          break;
      }
    },

    /**
     * Switch from the citation form to the extract article form
     * so the user can spawn a new document from an external link citation
     */
    async goToAnnotate() {
      this.mode = 'annotate';
      UIkit.modal(this.$refs.toAnnotateModal).show();
      dragElement(document.getElementById('moveableBasisForm'), 'basisFormModalId');

      //
      // setup form values
      if (this.citation.content !== null) {
        this.$refs.basisForm.setBasisFormData({
          discussiontitle: this.citation.title,
          initialStance: 'Host',
          documentType: 'custom',
          contentFormat: 'document',
          file: {
            name: this.citation.url,
            data: this.citation.content,
            type:
              this.citation.url.includes('.txt') || this.citation.url.includes('.docx')
                ? 'file'
                : 'text',
          },
          publication: null,
          author: null,
          date: null,
        });
      } else {
        this.$refs.basisForm.setBasisFormData({
          discussiontitle: this.citation.title,
          initialStance: 'Host',
          documentType: 'custom',
          contentFormat: 'document',
          file: {
            name: this.citation.url,
            data: null,
            type: 'url',
          },
          publication: null,
          author: null,
          date: null,
        });
        //
        // begin extracting article data
        this.$refs.basisForm.extractArticle();
      }
    },
    /**
     * save newly created citation to DB
     */
    async doAddNewCitation(citation) {
      this.actionStatus = LoadingStatus.LOADING;
      citation.parentId = this.parentId;
      citation.parentType = this.parentType;
      let res;
      if (citation.file.data) {
        if (citation.file.type === 'text') {
          citation.plain_text_content = citation.file.data;
          citation.file = '';
        } else citation.file = citation.file.data;
        let citationForm = new FormData();
        for (const key in citation) {
          const data = citation[key];
          citationForm.append(key, data);
        }
        res = await this.addCitation({
          citationForm,
          failed: () => {
            this.actionStatus = LoadingStatus.FAILED;
            EventBus.emit('citation.failed', `Failed to create new citation "${citation?.title}"`);
          },
        });
      } else {
        delete citation.file;
        let citationForm = citation;
        res = await this.addCitation({
          citationForm,
          failed: () => {
            this.actionStatus = LoadingStatus.FAILED;
            EventBus.emit('citation.failed', `Failed to create new citation "${citation?.title}"`);
          },
        });
      }
      if (!res) {
        this.actionStatus = LoadingStatus.FAILED;
        EventBus.emit('citation.failed', `Failed to create new citation "${citation?.title}"`);
      } else {
        if (this.citations[res.parentId]) {
          this.citations[res.parentId].splice(this.citations[res.parentId].length, 0, res);
        } else {
          this.citations[res.parentId] = [res];
        }
        this.actionStatus = LoadingStatus.SUCCESS;
        EventBus.emit('citation.saved', res);
        UIkit.modal(this.$refs.citationFormModal).hide();
      }
    },

    /**
     * update existing citaiton on DB
     */
    // async doUpdateCitation(citation) {
    //   this.actionStatus = LoadingStatus.LOADING;
    //   const res = await this.updateCitation({
    //     citationId: this.citation._id,
    //     updatesData: citation,
    //     failed: () => {
    //       this.actionStatus = LoadingStatus.FAILED;
    //       EventBus.emit('citation.failed');
    //     },
    //   });
    async doUpdateCitation(citation) {
      this.actionStatus = LoadingStatus.LOADING;
      let res;
      if (citation.file?.data) {
        if (citation.file.type === 'text') {
          citation.plain_text_content = citation.file.data;
          citation.file = '';
        } else if (citation.file.type === 'url') {
          citation.url = citation.file.data;
          citation.file = '';
        } else citation.file = citation.file.data;
        let citationForm = new FormData();
        for (const key in citation) {
          const data = citation[key];
          citationForm.append(key, data);
        }
        res = await this.updateCitation({
          citationId: this.citation._id,
          updatesData: citationForm,
          failed: () => {
            this.actionStatus = LoadingStatus.FAILED;
            EventBus.emit('citation.failed');
          },
        });
      } else {
        delete citation.file;
        let citationForm = citation;
        res = await this.updateCitation({
          citationId: this.citation._id,
          updatesData: citationForm,
          failed: () => {
            this.actionStatus = LoadingStatus.FAILED;
            EventBus.emit('citation.failed');
          },
        });
      }
      if (!res) {
        this.actionStatus = LoadingStatus.FAILED;
        EventBus.emit('citation.failed', `Failed to update citation "${citation?.title}"`);
      } else {
        const updatedCitationIndex = this.citations[res.parentId].findIndex(c => c._id === res._id);
        this.citations[res.parentId].splice(updatedCitationIndex, 1, res);
        this.actionStatus = LoadingStatus.SUCCESS;
        EventBus.emit('citation.saved', res);
        UIkit.modal(this.$refs.citationFormModal).hide();
      }
    },

    /**
     * Spawn a new document from the provided citation
     */
    async doAnnotateCitation(documentData, customFields) {
      this.submitDiscussionStatus = LoadingStatus.LOADING;
      const res = await this.loadCitationDocument({
        citation: { ...this.citation, disputed: true, disputedBy: this.userData.id },
        documentData,
        failed: () => {
          EventBus.emit('citation.failed');
        },
      });
      if (!res) {
        EventBus.emit('citation.failed');
      } else {
        if (customFields?.filter(field => field.name && field.value).length) {
          try {
            await this.updateTeam({
              teamID: res.team.team,
              updatedData: { custom_fields: customFields },
            });
          } catch (err) {
            console.error(err);
            this.submitDiscussionStatus = LoadingStatus.FAILED;
          }
        }

        if (!res.document) {
          EventBus.emit('citation.failed', 'failed to load citation document.');
          this.submitDiscussionStatus = LoadingStatus.FAILED;
        } else {
          this.submitDiscussionStatus = LoadingStatus.SUCCESS;
          const citationIdx = this.citations[res.parentId].findIndex(
            citation => getId(citation) === getId(res)
          );
          this.citations[res.parentId].splice(citationIdx, 1, res);
          EventBus.emit('citation.disputed', res);
          UIkit.modal(this.$refs.toAnnotateModal).hide();
          window.open(`/#${this.$router.routesMap.Debate(getId(res.document))}`);
          this.submitDiscussionStatus = LoadingStatus.NONE;
        }
      }
    },

    /**
     * Close toAnnotateModal modal
     */
    closeToAnnotateModal() {
      UIkit.modal(this.$refs.toAnnotateModal).hide();
    },

    /**
     * Save citation data to DB
     */
    async submitCitationForm() {
      this.actionStatus = LoadingStatus.LOADING;
      await this.$refs.citationForm.submitForm();
    },

    /**
     * Begin annotation process for citation
     */
    async submitToAnnotateForm() {
      this.actionStatus = LoadingStatus.LOADING;
      await this.$refs.toAnnotateForm.submitForm();
    },

    citationFormInvalid(value) {
      this.citationModalButtonsDef.Primary.disabled = value;
    },

    /**
     * Remove citation from DB
     */
    async removeCitation(citation, parent, parentType) {
      this.assignValues({
        citation,
        parent,
        parentType,
      });

      try {
        const citationRemovedId = await this.deleteCitation({
          citationId: this.citation._id,
          failed: () => {
            this.actionStatus = LoadingStatus.FAILED;
          },
        });

        if (!citationRemovedId) {
          this.actionStatus = LoadingStatus.FAILED;
        }
        const citationIdx = this.citations[this.citation.parentId].findIndex(
          c => c._id === citationRemovedId?._id
        );
        this.citations[this.citation.parentId].splice(citationIdx, 1);

        EventBus.emit('citation.saved');
      } catch (err) {
        this.actionStatus = LoadingStatus.FAILED;
      }
    },
  },
};
</script>

<style lang="scss">
.citemodalPosition {
  left: 28%;
  padding: 0;
  resize: horizontal;
  overflow: hidden;
  min-width: 860px;
  position: absolute !important;
}
</style>
